import { Alert, Button, NavigationLink } from '@ui/themed';
import { t } from 'i18next';
import { INTERCOM_TRIGGER } from '../../../constants/intercom';
import { useHandlers } from '../../../context/page-context';
import { useSalesAgentsAvailability } from '../hooks/use-sales-agent-availability';
import type { SalesAgentsAvailability } from '../types/sales-agent';

function UpgradePlanButton() {
  return (
    <Button variant="secondary" className={INTERCOM_TRIGGER.contact}>
      {t('features:customers.components.sales_agent_availability_alert.actions.add_agents')}
    </Button>
  );
}

function AddSalesAgentButton() {
  const { navigationHandler } = useHandlers();
  return (
    <NavigationLink onClick={() => navigationHandler.navigate('/customers/list', { newTab: true })}>
      {t('features:customers.components.sales_agent_availability_alert.actions.go_to_customers')}
    </NavigationLink>
  );
}

/**
 * Determine the alert variant, title, description and button based on sales agents availability
 */
const SALES_AGENT_ALERT_AVAILABILITY_VARIANT = (
  availability: SalesAgentsAvailability,
): {
  title: string;
  description: string;
  variant: 'success' | 'warning' | 'error';
  button?: JSX.Element;
} => {
  const { max, used, remaining } = availability;

  if (remaining > 0) {
    return {
      title: t(
        'features:customers.components.sales_agent_availability_alert.variants.is_remaining.title',
        {
          max_sales_agents: max,
        },
      ),
      description: t(
        'features:customers.components.sales_agent_availability_alert.variants.is_remaining.description',
        {
          max_sales_agents: max,
          used_sales_agents: used,
        },
      ),
      variant: 'success',
      button: <AddSalesAgentButton />,
    };
  }

  return {
    title: t(
      'features:customers.components.sales_agent_availability_alert.variants.none_remaining.title',
      {
        max_sales_agents: max,
      },
    ),
    description: t(
      'features:customers.components.sales_agent_availability_alert.variants.none_remaining.description',
      {
        max_sales_agents: max,
      },
    ),
    variant: 'warning',
    button: <UpgradePlanButton />,
  };
};

export function SalesAgentAvailabilityAlert() {
  const { data: availability, isLoading, error } = useSalesAgentsAvailability();

  if (isLoading) {
    return null;
  }

  if (error || !availability || availability.used === 0 || availability.max === -1) {
    // In case of error, we don't show the alert for now
    return null;
  }

  const VARIANT = SALES_AGENT_ALERT_AVAILABILITY_VARIANT(availability);

  return (
    <Alert variant={VARIANT.variant} headingLevel="h2">
      <div className="flex flex-col gap-y-2">
        <span>{VARIANT.description}</span>
        <div className="start-auto w-fit shrink-0">{VARIANT.button}</div>
      </div>
    </Alert>
  );
}
